import { Chip, Tooltip, Stack, Menu, MenuItem } from "@mui/material";
import { TrailerTypeAvatar } from "../../../pages/UpcomingPage/trailer-type-avatar";
import { useSelector } from "react-redux";
import { TrailerHover } from "../../modals/trailers/trailer-hover";
import { formatCustomDateString } from "../../../helpers/date-helper";
import RouteDrivers from "./RouteDrivers";
import AddDrivers from "./AddDrivers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  selectRouteHasSplittableCargos,
  selectRouteViewModel,
} from "../../../ducks/data/route-views/selectors";
import {
  addDriver,
  removeDriver,
  updateRouteTruck,
} from "../../../ducks/routes";
import { useAppDispatch } from "../../../redux-store";
import "./RouteHeader.css";
import VehicleListAutocompleteForRoute from "../../modals/trailers/VehicleListAutocompleteForRoute";
import { selectDrivers } from "../../../ducks/data/drivers/selectors";
import { useFeature } from "../../../hooks";
import React, { useMemo, useState } from "react";
import SplitRouteOrCargoDialog from "../../modals/CreateEditShipmentDialog/SplitRouteOrCargoDialog";
import UnsplitRouteDialog from "../../modals/CreateEditShipmentDialog/UnsplitRouteDialog";
import { selectCargoViews } from "../../../ducks/data/cargo-views/selectors";
import { selectSplitCargoTeamId } from "../../../ducks/data/org-settings/selectors";
import { CalendarTodayOutlined, MoreHoriz } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

interface Props {
  routeId: string;
  subheader?: React.ReactNode;
  openCloseIcon?: React.ReactNode;
  onClose?: () => void;
  isWide?: boolean;
}

const RouteHeader = (props: Props) => {
  const dispatch = useAppDispatch();
  const routeViewModel = useSelector(selectRouteViewModel(props.routeId));
  const drivers = useSelector(selectDrivers);
  const { t } = useTranslation(["cargo"]);
  const splitTeamId = useSelector(selectSplitCargoTeamId);
  const routeDrivers =
    useSelector(selectRouteViewModel(props.routeId))?.drivers || null;
  const liteVersion = useFeature("lite-version");
  const splitCargoButtonEnabled = useFeature("split-cargos-and-routes");
  const routeHasSplittableCargos = useSelector(
    selectRouteHasSplittableCargos(props.routeId)
  );
  const cargoViews = useSelector(
    selectCargoViews(routeViewModel?.cargoOrder ?? [])
  );
  const hasAnySplitCargo = useMemo(
    () => cargoViews.some((c) => c?.hasBeenSplit),
    [cargoViews]
  );
  const shouldShowSplitRouteButton = useMemo(
    () =>
      !liteVersion &&
      splitCargoButtonEnabled &&
      splitTeamId &&
      routeHasSplittableCargos,
    [
      liteVersion,
      splitCargoButtonEnabled,
      splitTeamId,
      routeHasSplittableCargos,
    ]
  );
  const shouldShowUnsplitRouteButton = useMemo(
    () => !liteVersion && splitCargoButtonEnabled && hasAnySplitCargo,
    [liteVersion, splitCargoButtonEnabled, hasAnySplitCargo]
  );
  const [splitCargoDialogOpen, setSplitCargoDialogOpen] = useState(false);
  const [unsplitRouteDialogOpen, setUnsplitRouteDialogOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const dropdownItems = [
    ...(shouldShowSplitRouteButton
      ? [
          {
            label: t("cargo:split"),
            onClick: () => setSplitCargoDialogOpen(true),
          },
        ]
      : []),
    ...(shouldShowUnsplitRouteButton
      ? [
          {
            label: t("cargo:unsplit"),
            onClick: () => setUnsplitRouteDialogOpen(true),
          },
        ]
      : []),
  ];

  const startDate = routeViewModel?.startDate
    ? formatCustomDateString(routeViewModel.startDate, "dd.MM")
    : "--/--";
  const endDate = routeViewModel?.endDate
    ? formatCustomDateString(routeViewModel.endDate, "dd.MM")
    : "--/--";

  if (!routeViewModel) {
    return null;
  }

  const onDriverClick = (driverId: string, checked: boolean) => {
    const args = { routeId: props.routeId, driverId };
    return dispatch(checked ? addDriver(args) : removeDriver(args))
      .unwrap()
      .then(() => {
        if (checked) {
          const driver = drivers.find((d) => d.id === driverId);
          if (!driver) {
            console.error(`Driver with id ${driverId} not found`);
            return;
          }
          if (!routeViewModel.truckId && driver.defaultTrailerId) {
            dispatch(
              updateRouteTruck({
                id: routeViewModel.id,
                truckId: driver.defaultTrailerId,
              })
            );
          }
        }
      })
      .catch(() => {
        // The thunk has already reported it and shown the error toast.
      });
  };

  return (
    <>
      {splitCargoDialogOpen && (
        <SplitRouteOrCargoDialog
          cargosIds={routeViewModel.cargoOrder}
          isSplitRoute
          routeId={props.routeId}
          onClose={() => setSplitCargoDialogOpen(false)}
        />
      )}
      {unsplitRouteDialogOpen && (
        <UnsplitRouteDialog
          routeId={props.routeId}
          cargoOrder={routeViewModel.cargoOrder}
          onClose={() => setUnsplitRouteDialogOpen(false)}
        />
      )}
      <div className="route-header">
        <div className="route-header__avatar">
          <TrailerAvatar trailerId={routeViewModel.truckId} />
        </div>
        <div className="route-header__name">
          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
          >
            <div
              className="route-header__trailer-select"
              {...(liteVersion ? { style: { width: "100%" } } : {})}
            >
              <VehicleListAutocompleteForRoute
                routeId={props.routeId}
                type="truck"
              />
            </div>
            <div
              className="route-header__trailer-select"
              {...(liteVersion ? { style: { width: "100%" } } : {})}
            >
              <VehicleListAutocompleteForRoute
                routeId={props.routeId}
                type="trailer"
              />
            </div>
            {routeViewModel.note && (
              <Tooltip title={routeViewModel.note} placement="right">
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{
                    display: {
                      xs: "none",
                      lg: "block",
                    },
                  }}
                />
              </Tooltip>
            )}
          </Stack>
          <div className="route-header__route-name">{props.subheader}</div>
        </div>
        <div className="route-header__status">
          {!liteVersion && (
            <>
              <div className="hidden xl:block">
                <RouteDrivers routeId={props.routeId} />
              </div>
              <AddDrivers
                onDriverClick={onDriverClick}
                routeDrivers={routeDrivers}
                routeId={props.routeId}
              />
            </>
          )}
          {!liteVersion && (
            <div className="hidden xl:block">
              <Chip
                icon={
                  <CalendarTodayOutlined
                    style={{ fontSize: 14, marginLeft: 4 }}
                  />
                }
                label={`${startDate} - ${endDate}`}
                color="primary"
                size="small"
                sx={{ display: "flex", paddingLeft: 0.5 }}
              />
            </div>
          )}
          {dropdownItems.length > 0 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuAnchorEl(e.currentTarget);
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "0",
                  fontSize: "16",
                  height: "min-content",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <MoreHoriz />
              </button>
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={() => setMenuAnchorEl(null)}
              >
                {dropdownItems.map((item) => (
                  <MenuItem
                    key={item.label}
                    onClick={(e) => {
                      e.stopPropagation();
                      item.onClick();
                      setMenuAnchorEl(null);
                    }}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          {props.openCloseIcon}
        </div>
      </div>
    </>
  );
};

export const TrailerAvatar = ({ trailerId }: { trailerId: string | null }) => (
  <TrailerHover trailerId={trailerId}>
    <div>
      <TrailerTypeAvatar trailerId={trailerId} />
    </div>
  </TrailerHover>
);

export default RouteHeader;
